import React, { useState } from 'react';
import { useButton, useCurrency, useI18n } from '~Hooks';
import styled from '@emotion/styled';
import NumberInput from '~ComponentsImproved/Numpad';
import { useReportsApi } from '~Components/ReportsUniverse';
import { useCashDrawer } from '~Providers/CashDrawerProvider';
import { CashSource } from '~Hooks/useMetadata';
import { Box, GreenButton, RedButton } from '~Styles';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import Heading from '~ComponentsImproved/Typography/Heading';
import Icon from '~ComponentsImproved/Icon';
import MinusIcon from '~Assets/icons/remove.svg';
import AddIcon from '~Assets/icons/add.svg';

const getColor = (amount: number) => {
  if (amount < 0) {
    return 'var(--colorUIRed)';
  } else if (amount > 0) {
    return 'var(--colorStatusGreen)';
  } else {
    return 'var(--colorWhite)';
  }
};

const MinusButton = styled(RedButton)`
  height: 32px;
  min-height: 32px;
  width: 32px;
  min-width: 24px;
  padding: 0;
`;

const PlusButton = styled(GreenButton)`
  height: 32px;
  min-height: 32px;
  width: 32px;
  min-width: 24px;
  padding: 0;
`;

interface Props {
  // Only pick id/name which are needed for display
  cashSource: Pick<CashSource, 'id' | 'name'>;
  handleUpdate: (amount: number, sourceId?: string | undefined, reason?: string) => void;
  currentBalance: number;
}

const CashSourceComponent: React.FC<Props> = ({ cashSource, handleUpdate, currentBalance }) => {
  const { i18n } = useI18n();
  const currency = useCurrency();
  const reportsApi = useReportsApi();
  const cashDrawer = useCashDrawer();

  const [showNumericInput, setShowNumericInput] = useState('');

  const balance = currentBalance || 0;

  const numericInputTitle: { [key: string]: string } = {
    deposit: i18n('CashManagement.DepositToTender'),
    withdraw: i18n('CashManagement.WithdrawFromTender'),
    payout: i18n('CashManagement.PayoutCTA'),
  };

  const onSubmit = (amount: string) => {
    const processedAmount = Math.round(Number(amount) * 100);
    if (showNumericInput === 'deposit') {
      handleUpdate(processedAmount, cashSource.id);
      if (cashSource.id === 'DRAWER') {
        reportsApi.cashBoxOpened();
        cashDrawer.open();
      }
    } else {
      handleUpdate(-processedAmount, cashSource.id);
      if (cashSource.id === 'DRAWER') {
        reportsApi.cashBoxOpened();
        cashDrawer.open();
      }
    }
    setShowNumericInput('');
  };

  const withdrawButton = useButton({
    onClick: () => setShowNumericInput('withdraw'),
  });

  const depositButton = useButton({
    onClick: () => setShowNumericInput('deposit'),
  });

  return (
    <>
      <Box display="flex" justifyContent="space-between" fluid gap="small">
        <Box display="flex" fluid justifyContent="space-between" alignItems="center" gap="small">
          <Paragraph level={3} color="var(--colorWhite)">
            {cashSource.name}
          </Paragraph>
          <Heading level={4} color={getColor(balance)}>
            {currency.format(balance)}
          </Heading>
        </Box>

        <Box display="flex" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap="xs">
            <MinusButton {...withdrawButton}>
              <Icon src={MinusIcon} height="24px" width="24px" color="var(--colorWhite)" />
            </MinusButton>
            <PlusButton {...depositButton}>
              <Icon src={AddIcon} height="24px" width="24px" color="var(--colorWhite)" />
            </PlusButton>
          </Box>
        </Box>
      </Box>

      {showNumericInput && (
        <NumberInput
          decimal
          maxValue={
            showNumericInput === 'withdraw' || showNumericInput === 'payout'
              ? balance / 100
              : 999999
          }
          title={numericInputTitle[showNumericInput]}
          onClose={() => setShowNumericInput('')}
          onSubmit={(amount) => onSubmit(amount)}
        />
      )}
    </>
  );
};

export default React.memo(CashSourceComponent);
